import React, { useEffect, useState } from "react";
import { Row, Col } from "react-bootstrap";
import { FormattedMessage, useIntl } from "react-intl";
import { FormProvider, useForm } from "react-hook-form";
import { toast } from "react-toastify";
import moment from "moment";
import { getNotificationSettings, updateNotificationSettings } from "api";

import Checkbox from "components/atoms/Checkbox/Checkbox";
import CardNew from "components/molecules/Card/CardNew";
import Button from "components/atoms/Button/Button";
import Dropdown from "components/atoms/Dropdown/Dropdown";
import errorToast from "helpers/errorToast";
import testIds from "constants/testIds";
import NotificationEmailDelegate from "./NotificationEmailDelegate";
import NotificationApprovalDelegate from "./NotificationApprovalDelegate";
import "./notifications.scss";

import {
  getBillingNotificationCustomDefaultOptions,
  getMobileNotificationCustomDefaultOptions,
  getNotificationOptions,
  getRadioOptions,
  getRateManagementCustomDefaultOptions,
  getRequestNotificationCustomDefaultOptions,
  getShipmentNotificationCustomDefaultOptions,
} from "./options";
import NotificationCustomOptions from "../NotificationCustomOptions";

const findResData = (arr, defaultOption) => {
  const found = arr.find((item) => item.key === defaultOption.key);
  return found ? found.value : defaultOption.value;
};

const Notifications = function () {
  const intl = useIntl();
  const methods = useForm();
  const [fetching, setFetching] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => getUserNotificationSettings(), []);

  const getUserNotificationSettings = () => {
    setFetching(true);
    getNotificationSettings()
      .then((res) => {
        setFetching(false);
        methods.setValue("settings", res.data.settings);
        methods.setValue("delegates", res.data.delegates);
        if (res.data.delegates?.delegate_expiration_time) {
          methods.setValue(
            "delegates.delegate_expiration_time",
            moment(res.data.delegates.delegate_expiration_time, "HH:mm"),
          );
        }
        if (res.data.delegates?.approver?.approval_delegate_expiration_time) {
          methods.setValue(
            "delegates.approver.approval_delegate_expiration_time",
            moment(res.data.delegates.approver.approval_delegate_expiration_time, "HH:mm"),
          );
        }

        const data = [
          {
            responseData: res.data.custom?.rate,
            defaultKeys: getRateManagementCustomDefaultOptions(intl),
            formKey: "custom.rate",
          },
          {
            responseData: res.data.custom?.request,
            defaultKeys: getRequestNotificationCustomDefaultOptions(intl),
            formKey: "custom.request",
          },
          {
            responseData: res.data.custom?.shipment,
            defaultKeys: getShipmentNotificationCustomDefaultOptions(intl),
            formKey: "custom.shipment",
          },
          {
            responseData: res.data.custom?.billing,
            defaultKeys: getBillingNotificationCustomDefaultOptions(intl),
            formKey: "custom.billing",
          },
          {
            responseData: res.data.custom?.mobile_push,
            defaultKeys: getMobileNotificationCustomDefaultOptions(intl),
            formKey: "custom.mobile_push",
          },
        ];

        data.forEach((item) => {
          if (item.responseData?.length) {
            const values = item.defaultKeys.map((defaultOption) => ({
              ...defaultOption,
              value: findResData(item.responseData, defaultOption),
            }));
            methods.setValue(item.formKey, values);
          }
        });
      })
      .catch((err) => {
        setFetching(false);
        errorToast(
          err,
          intl.formatMessage({
            id: "settings.couldNotRetrieveSettings",
            defaultMessage: "Could not retrieve settings. Please try again later",
          }),
        );
      });
  };

  const handleSaveNotificationSettings = (data) => {
    const {
      settings,
      delegates,
      delegates: { approver },
      custom,
    } = data;

    setIsSubmitting(true);
    const bodyData = {
      settings: {
        ...settings,
      },
      delegates: {
        email_delegate: delegates.email_delegate,
        ...(delegates.email_delegate && {
          delegate_email_address: delegates.delegate_email_address,
          delegate_expiration_date: moment(delegates.delegate_expiration_date).format("YYYY-MM-DD"),
          delegate_expiration_time: delegates.delegate_expiration_time,
        }),
        approver: {
          approval_delegate: approver.approval_delegate,
          ...(approver.approval_delegate && {
            approval_delegate_email: approver.approval_delegate_email,
            approval_delegate_expiration_date: moment(approver.approval_delegate_expiration_date).format("YYYY-MM-DD"),
            approval_delegate_expiration_time: approver.approval_delegate_expiration_time,
          }),
        },
      },
      custom: {
        ...(settings.rate_notifications === "custom" && { rate: custom.rate }),
        ...(settings.request_notifications === "custom" && { request: custom.request }),
        ...(settings.shipment_notifications === "custom" && { shipment: custom.shipment }),
        ...(settings.billing_notifications === "custom" && { billing: custom.billing }),
        ...(settings.mobile_push_notifications === "custom" && { mobile_push: custom.mobile_push }),
      },
    };

    updateNotificationSettings(bodyData)
      .then(({ data }) => {
        setIsSubmitting(false);
        if (data?.settings?.rate_notifications) {
          methods.setValue("settings.rate_notifications", data?.settings?.rate_notifications);
        }
        toast(
          intl.formatMessage({
            id: "settings.notifications.notificationsUpdateSuccessMessage",
            defaultMessage: "Notifications setting updated sucessfully",
          }),
          { type: "success" },
        );
      })
      .catch((err) => {
        setIsSubmitting(false);
        errorToast(
          err,
          intl.formatMessage({
            id: "settings.your-details.couldNotSaveSettings",
            defaultMessage: "Could not save settings. Please try again later",
          }),
        );
      });
  };

  const notificationsData = [
    {
      label: <FormattedMessage id="settings.notifications.rateManagement" defaultMessage="Rate management" />,
      settingsKey: "settings.rate_notifications",
      formKey: "custom.rate",
      defaultKeys: getRateManagementCustomDefaultOptions(intl),
    },
    {
      label: <FormattedMessage id="settings.notifications.requestNotifications" defaultMessage="Request notifications" />,
      settingsKey: "settings.request_notifications",
      formKey: "custom.request",
      defaultKeys: getRequestNotificationCustomDefaultOptions(intl),
    },
    {
      label: <FormattedMessage id="settings.notifications.shipmentNotifications" defaultMessage="Shipment notifications" />,
      settingsKey: "settings.shipment_notifications",
      formKey: "custom.shipment",
      defaultKeys: getShipmentNotificationCustomDefaultOptions(intl),
    },
    {
      label: <FormattedMessage id="settings.notifications.billingNotifications" defaultMessage="Billing notifications" />,
      settingsKey: "settings.billing_notifications",
      formKey: "custom.billing",
      defaultKeys: getBillingNotificationCustomDefaultOptions(intl),
    },
    {
      label: (
        <FormattedMessage id="settings.notifications.mobilePushNotifications" defaultMessage="Mobile push notifications" />
      ),
      settingsKey: "settings.mobile_push_notifications",
      formKey: "custom.mobile_push",
      defaultKeys: getMobileNotificationCustomDefaultOptions(intl),
    },
  ];

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(handleSaveNotificationSettings)}>
        <CardNew isLoading={fetching} className="notifications-container">
          <Row className="mb-3">
            <Col sm={3} className="font-weight-bold font-b1 mb-xs-3">
              <FormattedMessage id="settings.notifications.activityAndIdentity" defaultMessage="Activity and Identity" />
            </Col>
            <Col sm={9}>
              <Checkbox
                providerSections
                key={methods.watch("settings.weekly_activity_summary")}
                checkboxLabel={intl.formatMessage({
                  id: "settings.notifications.receiveWeeklyActivitySummary",
                  defaultMessage: "Receive weekly activity summary",
                })}
                name="settings.weekly_activity_summary"
              />
              <NotificationEmailDelegate />
              <NotificationApprovalDelegate />
            </Col>
          </Row>

          {notificationsData.map((item) => (
            <>
              <div key={item.settingsKey} className="settings-divider" />
              {methods.watch(item.settingsKey) !== null && (
                <Row className="mt-4 mb-4">
                  <Col sm={3} className="font-weight-bold font-b1 mb-xs-2">
                    {item.label}
                  </Col>
                  <Col sm={9}>
                    <div style={{ width: "100%", maxWidth: "392px" }}>
                      <Dropdown
                        useHookForm
                        name={item.settingsKey}
                        eventContext="Settings - Notifications"
                        options={getNotificationOptions(intl)}
                        required
                      />
                    </div>
                    {methods.watch(item.settingsKey) === "custom" ? (
                      <NotificationCustomOptions
                        radioOptions={getRadioOptions(intl)}
                        formKey={item.formKey}
                        defaultKeys={item.defaultKeys}
                      />
                    ) : null}
                  </Col>
                </Row>
              )}
            </>
          ))}

          <div className="settings-divider" />

          <Row className="mt-4">
            <Col sm={3} />
            <Col sm={9}>
              <Button
                type="submit"
                variant="success"
                className="ml-auto"
                disabled={isSubmitting}
                data-testid={testIds.NOTIFICATIONS_SAVE_BUTTON}
              >
                <FormattedMessage
                  id={isSubmitting ? "general.saving" : "general.save"}
                  defaultMessage={isSubmitting ? "Saving" : "Save"}
                />
              </Button>
            </Col>
          </Row>
        </CardNew>
      </form>
    </FormProvider>
  );
};

export default Notifications;
